import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../../global/transform-tag-name';
import { MULTI_VARIANT } from '../combobox.constants';

/**
 * @slot - label of the selection item has to be placed here (required)
 * @slot meta - Pass additional data to list item (optional)
 */
@Component({
  tag: 'wb-combobox-list-item',
  styleUrl: './combobox-list-item.scss',
  shadow: true,
})
export class ComboboxListItemComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Status of selection. */
  @Prop({ reflect: true, mutable: true })
  public selected: boolean;

  /** Indicates if item is disabled. */
  @Prop({ reflect: true })
  public disabled?: boolean;

  /**
   * Indicates if item is hidden.
   * @internal
   */
  @Prop({ reflect: true })
  public hidden: boolean = false;

  /**
   * Value of the item. Can be used as technical value for the selection.
   */
  @Prop({ reflect: true })
  public value: any;

  /**
   * Computed label of the list item. Only used for getting the selected value.
   * @internal
   */
  @Prop({ mutable: true })
  public label: any;

  /** Emitted when the selection changes. */
  @Event()
  public wbcomboboxlistitemselectionchange: EventEmitter<{ value: any }>;

  @State() private isMultiSelect = false;

  private comboBoxListEl: HTMLWbComboboxListElement;
  private TagNames = transformTagNames(this, 'wb-combobox-list-item', ['wb-icon', 'wb-checkbox', 'wb-list-group-item']);

  connectedCallback() {
    this.comboBoxListEl = this.el.parentElement as HTMLWbComboboxListElement;
    this.isMultiSelect = this.comboBoxListEl.variant === MULTI_VARIANT;
  }

  @Watch('selected')
  public onSelectedChange(newValue: boolean, oldValue: boolean = false): void {
    const selectedChanged = newValue !== oldValue;

    if (selectedChanged) {
      this.wbcomboboxlistitemselectionchange.emit({ value: newValue });
    }
  }

  private handleClick = (): void => {
    if (this.disabled) {
      return;
    }

    this.selected = !this.selected;
  };

  private updateLabel = (evt: Event) => {
    const target = evt.target as HTMLSlotElement;
    let label = '';

    target.assignedNodes().forEach((node, i) => {
      label = i > 0 ? `${label} ${node.textContent}` : node.textContent;
    });

    this.label = label;
  };

  public render(): JSX.Element {
    const { WbIcon, WbCheckbox, WbListGroupItem } = this.TagNames;

    return (
      <Host class="wb-combobox-list-item">
        {this.isMultiSelect ? (
          <WbListGroupItem customTabindex="0">
            <WbCheckbox slot="checkbox" disabled={this.disabled} checked={this.selected} onWbchange={this.handleClick}>
              <slot onSlotchange={this.updateLabel}></slot>
            </WbCheckbox>
            <slot slot="meta" name="meta" />
          </WbListGroupItem>
        ) : (
          <WbListGroupItem interactive={!this.disabled} onClick={this.handleClick}>
            {this.selected ? <WbIcon slot="meta-icon" name="bds/checkmark/24"></WbIcon> : null}
            <slot onSlotchange={this.updateLabel}></slot>
            <slot slot="meta" name="meta" />
          </WbListGroupItem>
        )}
      </Host>
    );
  }
}
